/**
 * IMPORTANT: Do not import anything else into this file from this directory
 * besides ./types; likely to create a circular dependency.
 */
import { DocumentNode } from 'graphql';
import { IconName } from '../react/icons';
import {
    ExternalFinancialEntitiesMap,
    ExternalFinancialEntityType,
    FinancialCategory,
    FinancialType,
} from './types';

export interface BaseConfig {
    iconName: IconName;
    title: string;
    color: string;
    query?: DocumentNode;
    isLinkable: boolean;
    financialType: FinancialType;
    category: FinancialCategory;
    canBeJoint: boolean;
}

// Map of financial entities' type : { ...config }
export const feBaseConfigs: ExternalFinancialEntitiesMap<BaseConfig> =
    Object.freeze({
        CashHolding: {
            iconName: 'BankNote02',
            title: 'Cash',
            color: '#97d495',
            isLinkable: false,
            financialType: 'asset',
            category: 'CashAccount',
            canBeJoint: true,
        },
        CheckingAccount: {
            iconName: 'Bank',
            title: 'Checking Account',
            color: '#97d495',
            isLinkable: true,
            financialType: 'asset',
            category: 'CashAccount',
            canBeJoint: true,
        },
        SavingsAccount: {
            iconName: 'PiggyBank02',
            title: 'Savings Account',
            color: '#97d495',
            isLinkable: true,
            financialType: 'asset',
            category: 'CashAccount',
            canBeJoint: true,
        },
        MoneyMarketAccount: {
            iconName: 'CoinsStacked01',
            // N.B. Money Market Account doesn't fit as an input placeholder
            title: 'Money Market',
            color: '#97d495',
            isLinkable: true,
            financialType: 'asset',
            category: 'CashAccount',
            canBeJoint: true,
        },
        CD: {
            iconName: 'Coins04',
            title: 'CD',
            color: '#97d495',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: true,
        },
        BrokerageAccount: {
            iconName: 'BarChart12',
            title: 'Brokerage Account',
            color: '#e9a7e2',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: true,
        },
        RetirementAccount: {
            iconName: 'Umbrella03',
            title: 'Retirement Account',
            color: '#9c8cfa',
            isLinkable: true,
            financialType: 'asset',
            category: 'Retirement',
            canBeJoint: false,
        },
        S529Account: {
            iconName: 'GraduationHat01',
            title: '529c Account',
            color: '#9c8cfa',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: false,
        },
        HSA: {
            iconName: 'HeartSquare',
            title: 'HSA Account',
            color: '#e9a7e2',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: true,
        },
        StockPlan: {
            iconName: 'PresentationChart02',
            title: 'Stock Plan',
            color: '#e9a7e2',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: true,
        },
        RealEstateProperty: {
            iconName: 'Home02',
            title: 'House',
            color: '#8bdde2',
            isLinkable: false,
            financialType: 'asset',
            category: 'OtherAsset',
            canBeJoint: true,
        },
        Car: {
            iconName: 'Car01',
            title: 'Car',
            color: '#8bdde2',
            isLinkable: false,
            financialType: 'asset',
            category: 'OtherAsset',
            canBeJoint: true,
        },
        OtherAsset: {
            iconName: 'Diamond01',
            title: 'Other Asset',
            color: '#8bdde2',
            isLinkable: false,
            financialType: 'asset',
            category: 'OtherAsset',
            canBeJoint: true,
        },
        OtherInvestment: {
            iconName: 'BarChart10',
            title: 'Other Investment',
            color: '#a7d0e9',
            isLinkable: true,
            financialType: 'asset',
            category: 'Investment',
            canBeJoint: true,
        },
        CreditCard: {
            iconName: 'CreditCard01',
            title: 'Credit Card',
            color: '#f18f8f',
            isLinkable: true,
            financialType: 'debt',
            category: 'CreditCard',
            canBeJoint: true,
        },
        HomeMortgage: {
            iconName: 'Certificate01',
            title: 'Home Mortgage',
            color: '#f18f8f',
            isLinkable: true,
            financialType: 'debt',
            category: 'Debt',
            canBeJoint: true,
        },
        CarLoan: {
            iconName: 'Passport',
            title: 'Car Loan',
            color: '#f18f8f',
            isLinkable: false,
            financialType: 'debt',
            category: 'Debt',
            canBeJoint: true,
        },
        StudentLoan: {
            iconName: 'Certificate02',
            title: 'Student Loan',
            color: '#f18f8f',
            isLinkable: true,
            financialType: 'debt',
            category: 'Debt',
            canBeJoint: false,
        },
        LineOfCredit: {
            iconName: 'CreditCardShield',
            title: 'Line of Credit',
            color: '#f18f8f',
            isLinkable: true,
            financialType: 'debt',
            category: 'Debt',
            canBeJoint: true,
        },
        OtherLiability: {
            iconName: 'Sale03',
            title: 'Other Debt',
            color: '#f18f8f',
            isLinkable: true,
            financialType: 'debt',
            category: 'Debt',
            canBeJoint: true,
        },
    });

export const getFEBaseConfig = <T extends ExternalFinancialEntityType>(
    type: T
) => {
    return feBaseConfigs[type];
};
